import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';

const GET_TEST_BY_ID = gql`
  query GetTestById($id: ID!) {
    test(id: $id) {
      id
      name
      description
      config {
        questionCount
        randomize
        timeLimit
        allowRetake
        retakeInterval
      }
      scoreRanges {
        minScore
        maxScore
        message
        imageUrl
        isPassed
      }
      questionSet {
        id
        name
      }
    }
  }
`;

const GET_TEST_QUESTIONS = gql`
  query GetTestQuestions($questionSetId: ID!) {
    questionSetItems(questionSetId: $questionSetId, forTest: true) {
      id
      questionId
      question {
        id
        title
        description
        type
        options {
          text
          order
        }
      }
      scoring {
        weight
        isRequired
        optionScores {
          optionIndex
          score
        }
      }
    }
  }
`;

const START_TEST = gql`
  mutation StartTest($testId: ID!) {
    startTest(testId: $testId) {
      success
      message
      testAttempt {
        id
        status
        startedAt
      }
      errors {
        field
        message
      }
    }
  }
`;

const FINISH_TEST = gql`
  mutation FinishTest($testAttemptId: ID, $testId: ID, $answers: [TestAnswerInput!]) {
    finishTest(testAttemptId: $testAttemptId, testId: $testId, answers: $answers) {
      success
      message
      testAttempt {
        id
        status
        totalScore
        result {
          message
          imageUrl
          isPassed
          score
        }
        timeSpent
        questionCount
        answeredCount
      }
      errors {
        field
        message
      }
    }
  }
`;

const TestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [testAttemptId, setTestAttemptId] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const { data, loading, error } = useQuery(GET_TEST_BY_ID, {
    variables: { id: testId },
    skip: !testId
  });

  const [startTest, { loading: startLoading }] = useMutation(START_TEST);
  const [finishTest, { loading: finishLoading }] = useMutation(FINISH_TEST);

  const test = data?.test;
  
  // 使用新的查詢來獲取測試題目（forTest=true）
  const { data: questionsData, loading: questionsLoading } = useQuery(GET_TEST_QUESTIONS, {
    variables: { questionSetId: test?.questionSet?.id },
    skip: !test?.questionSet?.id
  });

  const questions = questionsData?.questionSetItems || [];
  
  // 檢查是否還有未作答的題目
  const hasUnansweredQuestions = questions.length > 0;

  // 處理時間限制
  useEffect(() => {
    if (test?.config?.timeLimit && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleFinishTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, test]);

  const handleStartTest = async () => {
    console.log('Starting test:', testId);
    
    try {
      const result = await startTest({
        variables: { testId }
      });

      if (result.data.startTest.success) {
        console.log('Test started successfully:', result.data.startTest.testAttempt);
        setTestAttemptId(result.data.startTest.testAttempt.id);
        setIsStarted(true);
        
        // 如果沒有未作答的題目，直接完成測試
        if (!hasUnansweredQuestions) {
          console.log('No unanswered questions, completing test immediately');
          await handleFinishTest();
          return;
        }
        
        // 設定時間限制
        if (test.config.timeLimit > 0) {
          setTimeLeft(test.config.timeLimit * 60); // 轉換為秒
        }
      } else {
        console.log('Failed to start test:', result.data.startTest.message);
        alert('開始測試失敗：' + result.data.startTest.message);
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert('開始測試失敗，請稍後再試');
    }
  };

  const handleOptionSelect = (optionIndex) => {
    console.log('Option selected:', optionIndex);
    
    const currentQuestion = questions[currentQuestionIndex];
    const questionId = currentQuestion.question.id;
    
    if (currentQuestion.question.type === 'SINGLE_CHOICE') {
      // 單選題
      setSelectedOptions([optionIndex]);
      setAnswers(prev => ({
        ...prev,
        [questionId]: [optionIndex]
      }));
    } else {
      // 多選題
      setSelectedOptions(prev => {
        const newOptions = prev.includes(optionIndex)
          ? prev.filter(i => i !== optionIndex)
          : [...prev, optionIndex];
        
        setAnswers(prevAnswers => ({
          ...prevAnswers,
          [questionId]: newOptions
        }));
        
        return newOptions;
      });
    }
  };

  const handleNextQuestion = async () => {
    console.log('Moving to next question');
    
    // 統一簡化：前端自己管理答案，不需要調用後端
    // 答案已經保存在 answers 狀態中（通過 handleOptionSelect）
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOptions([]);
    } else {
      // 最後一題，完成測試
      await handleFinishTest();
    }
  };

  const handlePreviousQuestion = () => {
    console.log('Moving to previous question');
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      const currentQuestion = questions[currentQuestionIndex - 1];
      const questionId = currentQuestion.question.id;
      setSelectedOptions(answers[questionId] || []);
    }
  };

  const handleFinishTest = async () => {
    console.log('Finishing test');
    
    // 統一簡化：永遠發送所有答案，不保存記錄
    // 先保存最後一題的答案到本地狀態
    const currentQuestion = questions[currentQuestionIndex];
    const currentQuestionId = currentQuestion?.question?.id;
    const finalAnswers = { ...answers };
    if (selectedOptions.length > 0 && currentQuestionId) {
      finalAnswers[currentQuestionId] = selectedOptions;
    }
    
    // 構建所有答案數組（使用包含最後一題答案的 finalAnswers）
    const allAnswers = questions.map(q => ({
      questionId: q.question.id,
      selectedOptions: finalAnswers[q.question.id] || []
    }));
    
    try {
      const result = await finishTest({
        variables: { 
          testAttemptId: testAttemptId,
          testId: testId,
          answers: allAnswers
        }
      });

      if (result.data.finishTest.success) {
        console.log('Test completed successfully:', result.data.finishTest.testAttempt);
        setResult(result.data.finishTest.testAttempt.result);
        setIsCompleted(true);
      } else {
        console.log('Failed to finish test:', result.data.finishTest.message);
        alert('完成測試失敗：' + result.data.finishTest.message);
      }
    } catch (error) {
      console.error('Error finishing test:', error);
      alert('完成測試失敗，請稍後再試');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading || questionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入測試中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">載入測試失敗</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">測試不存在</p>
        </div>
      </div>
    );
  }

  if (!isStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{test.name}</h1>
          {test.description && (
            <p className="text-gray-600 mb-6">{test.description}</p>
          )}
          
          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">題目數量</span>
              <span className="font-semibold">{questions.length} 題</span>
            </div>
            
            {!hasUnansweredQuestions && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800">
                  <span className="font-semibold">注意：</span>您已經回答了所有題目，開始測試將直接計算分數。
                </p>
              </div>
            )}
            
            {test.config.timeLimit > 0 && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="text-gray-700">時間限制</span>
                <span className="font-semibold">{test.config.timeLimit} 分鐘</span>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">允許重複作答</span>
              <span className="font-semibold">{test.config.allowRetake ? '是' : '否'}</span>
            </div>
          </div>
          
          <button
            onClick={handleStartTest}
            disabled={startLoading}
            className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {startLoading ? '開始中...' : hasUnansweredQuestions ? '開始答題' : '查看分數'}
          </button>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">測試完成</h1>
          
          {result && (
            <div className="space-y-4 mb-8">
              {result.imageUrl && (
                <img 
                  src={result.imageUrl} 
                  alt="Result" 
                  className="mx-auto max-w-xs rounded-lg"
                />
              )}
              
              <div className="text-2xl font-bold text-blue-600">
                總分：{typeof result.score === 'number' ? result.score.toFixed(2) : result.score}
              </div>
              
              <div className="text-lg text-gray-700">
                {result.message}
              </div>
              
              {result.isPassed && (
                <div className="text-green-600 font-semibold">
                  ✓ 通過測試
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              重新作答
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
            >
              返回首頁
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  // 如果沒有題目，顯示等待完成的信息
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">正在計算分數</h2>
          <p className="text-gray-600">請稍候，系統正在處理您的測試結果...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 進度條 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              第 {currentQuestionIndex + 1} 題 / 共 {questions.length} 題
            </span>
            {timeLeft !== null && (
              <span className="text-sm text-gray-600">
                剩餘時間：{formatTime(timeLeft)}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* 答題區域 */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {currentQuestion.question.title}
          </h2>
          
          {currentQuestion.question.description && (
            <p className="text-gray-600 mb-6">{currentQuestion.question.description}</p>
          )}
          
          <div className="space-y-3 mb-8">
            {currentQuestion.question.options.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedOptions.includes(index)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type={currentQuestion.question.type === 'SINGLE_CHOICE' ? 'radio' : 'checkbox'}
                  name={`question-${currentQuestionIndex}`}
                  value={index}
                  checked={selectedOptions.includes(index)}
                  onChange={() => handleOptionSelect(index)}
                  className="mr-3"
                />
                <span className="text-gray-900">{option.text}</span>
              </label>
            ))}
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一題
            </button>
            
            <button
              onClick={handleNextQuestion}
              disabled={selectedOptions.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentQuestionIndex === questions.length - 1 ? '完成' : '下一題'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage; 